import { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import {
  FileText,
  User,
  Calendar,
  Clock,
  CheckCircle2,
  AlertCircle,
  Plus,
  Trash2,
  Search,
  Pill,
  ChevronDown,
  ChevronRight,
  ExternalLink,
  X,
  UserRound,
  Activity,
  ClipboardList,
  FileSignature,
  ShieldCheck,
  Loader2,
  Sparkles,
  Stethoscope,
  Send,
  HelpCircle,
  Check,
  Download,
  AlertTriangle,
  Info
} from 'lucide-react';
import toast from 'react-hot-toast';
import {
  ALL_INVESTIGATIONS,
  getFilteredInvestigations,
  groupInvestigationsByCategory,
  FREQUENCY_OPTIONS,
  STRENGTH_OPTIONS,
  TYPE_OPTIONS,
  DURATION_OPTIONS,
  INSTRUCTIONS_OPTIONS,
  UNIT_OPTIONS,
  DURATION_UNIT_OPTIONS,
  MEDICINE_OPTIONS
} from '../../constants/prescriptionConstants';
import DoxezLogo from '../../assets/Doxez.png';

// ─── Reusable UI Components ──────────────────────────────────────────────────
const Card = ({ children, className = '', id = '' }) => (
  <div id={id} className={`bg-white rounded-3xl border-2 border-slate-100 hover:border-slate-200/90 shadow-sm transition-all ${className}`}>
    {children}
  </div>
);

const CardHeader = ({ icon: Icon, iconBg = 'bg-[#143E78]/10', iconColor = 'text-[#143E78]', title, subtitle, action, badge }) => (
  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
    <div className="flex items-center gap-3">
      {Icon && (
        <div className={`w-10 h-10 rounded-2xl flex items-center justify-center font-black shrink-0 ${iconBg} ${iconColor} shadow-2xs`}>
          <Icon size={20} />
        </div>
      )}
      <div>
        <div className="flex items-center gap-2">
          <h3 className="text-base font-black text-slate-900 tracking-tight">{title}</h3>
          {badge && <div>{badge}</div>}
        </div>
        {subtitle && <p className="text-xs font-bold text-slate-400 mt-0.5">{subtitle}</p>}
      </div>
    </div>
    {action && <div className="flex items-center gap-2">{action}</div>}
  </div>
);

const Badge = ({ children, variant = 'default', size = 'sm', className = '' }) => {
  const variants = {
    default: 'bg-slate-100 text-slate-700 border-slate-200',
    primary: 'bg-[#143E78]/10 text-[#143E78] border-[#143E78]/20',
    success: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    warning: 'bg-amber-50 text-amber-700 border-amber-200',
    danger: 'bg-rose-50 text-rose-700 border-rose-200',
    info: 'bg-blue-50 text-blue-700 border-blue-200',
  };
  const sizes = {
    sm: 'px-2.5 py-1 text-[10px]',
    md: 'px-3 py-1.5 text-xs',
    lg: 'px-4 py-2 text-sm',
  };
  return (
    <span className={`inline-flex items-center font-black uppercase tracking-wider rounded-xl border ${variants[variant]} ${sizes[size]} ${className}`}>
      {children}
    </span>
  );
};

// ─── Main Component ──────────────────────────────────────────────────────────
const PrescriptionFormPublic = () => {
  const { id } = useParams();
  const { user, loading: authLoading } = useAuth();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [prescription, setPrescription] = useState(null);
  const [fetchError, setFetchError] = useState(null);
  const [fetchErrorStatus, setFetchErrorStatus] = useState(null);
  const [unauthorized, setUnauthorized] = useState(false);
  const [investigationSearch, setInvestigationSearch] = useState('');
  const [showInvestigationSuggestions, setShowInvestigationSuggestions] = useState(false);
  const [showAdvice, setShowAdvice] = useState(false);
  const [showNotes, setShowNotes] = useState(false);
  const [showClinicalInfo, setShowClinicalInfo] = useState(true);

  const [formData, setFormData] = useState({
    notes: '',
    advice: '',
    provisionalDiagnosis: '',
    followUp: '',
    chiefComplaints: '',
    pastMedicalHistory: '',
    comorbidities: '',
    prescriptionItems: [{ id: Date.now(), type: '', medicineName: '', strength: '', unit: 'mg', frequency: '', duration: '', durationUnit: 'days', instructions: '' }],
    investigations: []
  });
  const [errors, setErrors] = useState({});
  const [customFrequencyItems, setCustomFrequencyItems] = useState({});
  const [customTypeItems, setCustomTypeItems] = useState({});
  const [customDurationItems, setCustomDurationItems] = useState({});
  const [customInstructionsItems, setCustomInstructionsItems] = useState({});
  const [customUnitItems, setCustomUnitItems] = useState({});
  const [isDraftLoaded, setIsDraftLoaded] = useState(false);

  const didFetchRef = useRef(false);
  const isLoadedRef = useRef(false);
  const DRAFT_KEY = `prescription_form_draft_${id}`;

  const loadDraft = () => {
    try {
      const saved = localStorage.getItem(DRAFT_KEY);
      if (saved) {
        return JSON.parse(saved);
      }
    } catch (e) {
      console.warn('Failed to parse prescription draft from localStorage', e);
    }
    return null;
  };

  const saveDraft = (currentFormData, customState) => {
    try {
      const payload = {
        formData: currentFormData,
        customTypeItems: customState.customTypeItems,
        customFrequencyItems: customState.customFrequencyItems,
        customDurationItems: customState.customDurationItems,
        customInstructionsItems: customState.customInstructionsItems,
        customUnitItems: customState.customUnitItems,
        updatedAt: Date.now()
      };
      localStorage.setItem(DRAFT_KEY, JSON.stringify(payload));
    } catch (e) {
      console.warn('Failed to save prescription draft to localStorage', e);
    }
  };

  const clearDraft = () => {
    try {
      localStorage.removeItem(DRAFT_KEY);
    } catch (e) { }
  };

  useEffect(() => {
    didFetchRef.current = false;
  }, [id]);

  useEffect(() => {
    if (authLoading) return;
    if (!didFetchRef.current) {
      didFetchRef.current = true;
      fetchPrescriptionForm();
    }
  }, [id, authLoading]);

  // Auto-save live draft to localStorage when user makes changes
  useEffect(() => {
    if (!isLoadedRef.current || submitted) return;
    saveDraft(formData, {
      customTypeItems,
      customFrequencyItems,
      customDurationItems,
      customInstructionsItems,
      customUnitItems
    });
  }, [formData, customTypeItems, customFrequencyItems, customDurationItems, customInstructionsItems, customUnitItems, submitted]);

  const capitalizeFirst = (val) => {
    if (!val || typeof val !== 'string') return val;
    return val.charAt(0).toUpperCase() + val.slice(1);
  };

  const formatTime12Hour = (time24) => {
    if (!time24 || typeof time24 !== 'string') return '';
    if (time24.includes('-')) {
      const parts = time24.split('-');
      return parts.map(p => formatTime12Hour(p.trim())).join(' - ');
    }
    const clean = time24.trim();
    if (clean.toLowerCase().includes('am') || clean.toLowerCase().includes('pm')) return clean;
    const [hours, minutes] = clean.split(':').map(Number);
    if (isNaN(hours) || isNaN(minutes)) return clean;
    const period = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours % 12 || 12;
    return `${String(displayHours).padStart(2, '0')}:${String(minutes).padStart(2, '0')} ${period}`;
  };

  const formatAppointmentDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    if (typeof dateStr === 'string') {
      const trimmed = dateStr.trim();
      const isoMatch = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (isoMatch) {
        const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        const mIdx = parseInt(isoMatch[2], 10) - 1;
        return `${isoMatch[3]} ${monthNames[mIdx] || ''} ${isoMatch[1]}`;
      }
      const dmyMatch = trimmed.match(/^(\d{2})-(\d{2})-(\d{4})/);
      if (dmyMatch) {
        const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        const mIdx = parseInt(dmyMatch[2], 10) - 1;
        return `${dmyMatch[1]} ${monthNames[mIdx] || ''} ${dmyMatch[3]}`;
      }
    }
    const d = dateStr instanceof Date ? dateStr : new Date(dateStr);
    if (isNaN(d.getTime())) return 'N/A';
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const dd = String(d.getUTCDate()).padStart(2, '0');
    const mm = monthNames[d.getUTCMonth()] || '';
    const yyyy = d.getUTCFullYear();
    return `${dd} ${mm} ${yyyy}`;
  };

  const parseStrength = (val) => {
    if (!val) return { numeric: '', unit: 'mg' };
    const match = String(val).match(/^(\d+(?:\.\d+)?)\s*(mg|mcg|ml|gm|IU|%)?$/i);
    if (match) return { numeric: match[1], unit: match[2] || 'mg' };
    const numMatch = String(val).match(/^(\d+(?:\.\d+)?)/);
    if (numMatch) return { numeric: numMatch[1], unit: 'mg' };
    return { numeric: '', unit: 'mg' };
  };

  const parseDuration = (val) => {
    if (!val) return { numeric: '', unit: 'days' };
    const match = String(val).match(/^(\d+(?:\.\d+)?)\s*(day|week|month|year)s?$/i);
    if (match) return { numeric: match[1], unit: match[2].toLowerCase() + 's' };
    const numMatch = String(val).match(/^(\d+(?:\.\d+)?)/);
    if (numMatch) return { numeric: numMatch[1], unit: 'days' };
    return { numeric: '', unit: 'days' };
  };

  const fetchPrescriptionForm = async () => {
    try {
      const response = await api.get(`/prescriptions/${id}/form`);
      const data = response.data?.data;
      setPrescription(data);

      if (data) {
        const draft = loadDraft();
        if (draft && draft.formData && Array.isArray(draft.formData.prescriptionItems)) {
          setFormData(draft.formData);
          if (draft.customTypeItems) setCustomTypeItems(draft.customTypeItems);
          if (draft.customFrequencyItems) setCustomFrequencyItems(draft.customFrequencyItems);
          if (draft.customDurationItems) setCustomDurationItems(draft.customDurationItems);
          if (draft.customInstructionsItems) setCustomInstructionsItems(draft.customInstructionsItems);
          if (draft.customUnitItems) setCustomUnitItems(draft.customUnitItems);

          if (draft.formData.advice) setShowAdvice(true);
          if (draft.formData.notes) setShowNotes(true);
          setIsDraftLoaded(true);
        } else {
          setFormData({
            notes: data.notes || '',
            advice: data.advice || '',
            provisionalDiagnosis: data.provisionalDiagnosis || '',
            followUp: data.followUp || '',
            chiefComplaints: data.chiefComplaints || data?.leadId?.chiefComplaints || '',
            pastMedicalHistory: data.pastMedicalHistory || data?.leadId?.pastMedicalHistory || '',
            comorbidities: data?.leadId?.comorbidities || '',
            prescriptionItems: data.prescriptionItems?.length
              ? data.prescriptionItems.map((item, i) => {
                const s = parseStrength(item.strength);
                const d = parseDuration(item.duration);
                return {
                  id: item._id || Date.now() + i,
                  type: item.type || '',
                  medicineName: item.medicineName || '',
                  strength: item.strength || s.numeric,
                  unit: item.unit || s.unit || 'mg',
                  frequency: item.frequency || '',
                  duration: item.duration || d.numeric,
                  durationUnit: item.durationUnit || d.unit || 'days',
                  instructions: item.instructions || ''
                };
              })
              : [{ id: Date.now(), type: '', medicineName: '', strength: '', unit: 'mg', frequency: '', duration: '', durationUnit: 'days', instructions: '' }],
            investigations: data.investigations?.length ? data.investigations : []
          });

          if (data.advice) setShowAdvice(true);
          if (data.notes) setShowNotes(true);
        }
        isLoadedRef.current = true;
      }
    } catch (error) {
      const status = error.response?.status;
      setFetchErrorStatus(status);
      if (status === 401 || status === 403) {
        setUnauthorized(true);
        setFetchError('You are not authorized to view or edit this prescription.');
      } else {
        setFetchError(error.response?.data?.message || 'Failed to load prescription details.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDiscardDraft = () => {
    if (window.confirm('Are you sure you want to discard your unsaved draft and reload initial data?')) {
      clearDraft();
      window.location.reload();
    }
  };

  const addItem = () => {
    setFormData((prev) => ({
      ...prev,
      prescriptionItems: [
        ...prev.prescriptionItems,
        { id: Date.now(), type: '', medicineName: '', strength: '', unit: 'mg', frequency: '', duration: '', durationUnit: 'days', instructions: '' }
      ]
    }));
  };

  const removeItem = (itemId) => {
    if (formData.prescriptionItems.length === 1) {
      toast.error('Prescription must contain at least one medicine item');
      return;
    }
    setFormData((prev) => ({
      ...prev,
      prescriptionItems: prev.prescriptionItems.filter((item) => item.id !== itemId)
    }));
  };

  const handleItemChange = (index, field, value) => {
    setFormData((prev) => {
      const updated = [...prev.prescriptionItems];
      updated[index] = { ...updated[index], [field]: value };
      return { ...prev, prescriptionItems: updated };
    });

    if (errors[`item_${index}_${field}`]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[`item_${index}_${field}`];
        return next;
      });
    }
  };

  const addInvestigation = (investigation) => {
    const isDuplicate = formData.investigations.some(
      (inv) => inv.label.toLowerCase() === investigation.label.toLowerCase()
    );
    if (isDuplicate) {
      toast.error('Investigation already added');
      return;
    }

    setFormData((prev) => ({
      ...prev,
      investigations: [...prev.investigations, investigation]
    }));
    setInvestigationSearch('');
    setShowInvestigationSuggestions(false);
  };

  const removeInvestigation = (index) => {
    setFormData((prev) => ({
      ...prev,
      investigations: prev.investigations.filter((_, i) => i !== index)
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    const validItems = formData.prescriptionItems.filter(
      (item) => item.type || item.medicineName || item.strength || item.frequency || item.duration
    );

    if (validItems.length === 0) {
      newErrors.prescriptionItems = 'Please add at least one medicine to the prescription.';
    }

    formData.prescriptionItems.forEach((item, index) => {
      if (item.type || item.medicineName || item.strength || item.frequency || item.duration) {
        if (!item.medicineName.trim()) {
          newErrors[`item_${index}_medicineName`] = 'Medicine name is required';
        }
        if (!item.frequency) {
          newErrors[`item_${index}_frequency`] = 'Frequency is required';
        }
        if (!item.duration) {
          newErrors[`item_${index}_duration`] = 'Duration is required';
        }
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error('Please resolve the required fields marked in red.');
      return;
    }

    setSubmitting(true);
    try {
      const validItems = formData.prescriptionItems
        .filter((item) => item.medicineName && item.medicineName.trim())
        .map((item) => ({
          type: item.type ? item.type.trim() : undefined,
          medicineName: item.medicineName.trim(),
          strength: item.strength ? String(item.strength).trim() : undefined,
          unit: item.unit ? String(item.unit).trim() : undefined,
          frequency: item.frequency ? String(item.frequency).trim() : '1-0-1',
          duration: item.duration ? String(item.duration).trim() : '3',
          durationUnit: item.durationUnit ? String(item.durationUnit).trim() : 'days',
          instructions: item.instructions ? String(item.instructions).trim() : undefined
        }));

      const payload = {
        notes: formData.notes?.trim() || undefined,
        advice: formData.advice?.trim() || undefined,
        provisionalDiagnosis: formData.provisionalDiagnosis?.trim() || undefined,
        followUp: formData.followUp?.trim() || undefined,
        chiefComplaints: formData.chiefComplaints?.trim() || undefined,
        pastMedicalHistory: formData.pastMedicalHistory?.trim() || undefined,
        comorbidities: formData.comorbidities?.trim() || undefined,
        prescriptionItems: validItems,
        investigations: formData.investigations.map((inv) => ({
          label: inv.label,
          category: inv.category,
          icon: inv.icon
        }))
      };

      try {
        await api.post(`/prescriptions/${id}/form`, payload);
      } catch (err) {
        await api.patch(`/prescriptions/${id}/form`, payload);
      }
      clearDraft();
      toast.success('Prescription submitted and saved successfully!');
      setSubmitted(true);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to submit prescription');
    } finally {
      setSubmitting(false);
    }
  };

  const filteredInvestigations = getFilteredInvestigations(investigationSearch);
  const groupedInvestigations = groupInvestigationsByCategory(filteredInvestigations);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4 font-sans">
        <div className="text-center space-y-4 bg-white p-8 rounded-3xl border-2 border-slate-100 shadow-sm max-w-sm w-full">
          <div className="w-14 h-14 bg-[#143E78]/10 rounded-2xl flex items-center justify-center mx-auto text-[#143E78] shadow-sm">
            <Loader2 className="w-7 h-7 animate-spin" />
          </div>
          <div>
            <h3 className="text-base font-black text-slate-800 tracking-tight">Loading Prescription Hub</h3>
            <p className="text-xs font-bold text-slate-400 mt-1">Retrieving records and patient data...</p>
          </div>
        </div>
      </div>
    );
  }

  if (fetchError || unauthorized) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4 font-sans">
        <div className="max-w-md w-full bg-white rounded-3xl border-2 border-rose-100 p-8 shadow-sm text-center space-y-4">
          <div className="w-16 h-16 bg-rose-50 rounded-2xl flex items-center justify-center mx-auto text-rose-600 shadow-sm">
            <AlertCircle size={32} />
          </div>
          <div>
            <h2 className="text-lg font-black text-slate-900 tracking-tight">Access Restricted</h2>
            <p className="text-xs text-slate-500 font-medium mt-1 leading-relaxed">{fetchError}</p>
          </div>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4 font-sans">
        <div className="max-w-md w-full bg-white rounded-3xl border-2 border-emerald-100 p-8 shadow-sm text-center space-y-4 animate-in fade-in zoom-in-95">
          <div className="w-16 h-16 bg-emerald-50 rounded-2xl flex items-center justify-center mx-auto text-emerald-600 shadow-sm">
            <CheckCircle2 size={32} />
          </div>
          <div>
            <h2 className="text-xl font-black text-slate-900 tracking-tight">Prescription Submitted</h2>
            <p className="text-xs text-slate-500 font-medium mt-1.5 leading-relaxed">
              Your prescription has been recorded and submitted to the case file successfully.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const patientName = prescription?.appointmentId?.patientName || prescription?.leadId?.patientName || 'Patient Case';
  const appointmentDate = prescription?.appointmentId?.appointmentDate;
  const timeSlot = prescription?.appointmentId?.appointmentTimeSlot;
  const patientGender = prescription?.leadId?.patientGender;
  const patientAge = prescription?.leadId?.patientAge;
  const patientDocs = prescription?.leadId?.documents || [];

  return (
    <div className="min-h-screen bg-slate-50/80 font-sans pb-16 selection:bg-[#143E78] selection:text-white">
      {/* ─── Top Global Navigation Bar ───────────────────────────────── */}
      <header className="bg-white/90 backdrop-blur-md border-b border-slate-200/80 sticky top-0 z-30 shadow-2xs">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-18 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <img
              src={DoxezLogo}
              alt="Doxez Healthcare"
              className="h-11 sm:h-12 w-auto object-contain drop-shadow-xs hover:opacity-95 transition-opacity"
            />
          </div>
          <div className="flex items-center gap-2.5">
            {isDraftLoaded && (
              <button
                type="button"
                onClick={handleDiscardDraft}
                className="px-2.5 py-1 bg-slate-100 hover:bg-rose-50 hover:text-rose-600 text-slate-600 rounded-xl text-[10px] font-bold transition-all border border-slate-200 cursor-pointer"
                title="Discard unsaved local draft and reload original"
              >
                Reset Draft
              </button>
            )}
            <Badge variant="primary" size="md" className="gap-1.5 shadow-2xs">
              <ShieldCheck className="w-4 h-4 text-[#143E78]" />
              <span>Secure Prescription Form</span>
            </Badge>
          </div>
        </div>
      </header>

      {/* ─── Main Content Container ──────────────────────────────────── */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">

        {/* ─── 1. Patient Dossier & Prescription Header Card ───────────── */}
        <Card className="p-0 overflow-hidden">
          <div className="bg-gradient-to-r from-[#143E78]/5 via-[#143E78]/10 to-transparent p-5 sm:p-6 border-b border-slate-100">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div className="flex items-center gap-3.5">
                <div className="w-12 h-12 rounded-2xl bg-[#143E78] text-white flex items-center justify-center font-black shadow-md shadow-[#143E78]/20 shrink-0">
                  <FileText size={24} />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h1 className="text-xl font-black text-slate-900 tracking-tight">Prescription Form</h1>
                    <span className="px-2.5 py-0.5 bg-blue-100 text-blue-800 rounded-full text-[10px] font-black uppercase">
                      Order
                    </span>
                  </div>
                  <p className="text-xs font-bold text-slate-500 mt-0.5">
                    Fill out the patient prescription, medication dosage, instructions, and diagnostics below
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Patient Quick Info Pills */}
          <div className="p-5 sm:p-6 bg-white space-y-4">
            <div className="flex flex-wrap items-center gap-2.5">
              <div className="inline-flex items-center gap-2 px-3.5 py-2 bg-slate-50 border border-slate-200 rounded-2xl shadow-2xs">
                <UserRound className="w-4 h-4 text-[#143E78]" />
                <span className="text-xs font-black text-slate-900">{patientName}</span>
              </div>

              {appointmentDate && (
                <div className="inline-flex items-center gap-2 px-3.5 py-2 bg-slate-50 border border-slate-200 rounded-2xl shadow-2xs">
                  <Calendar className="w-4 h-4 text-blue-600" />
                  <span className="text-xs font-bold text-slate-800">{formatAppointmentDate(appointmentDate)}</span>
                </div>
              )}

              {timeSlot && (
                <div className="inline-flex items-center gap-2 px-3.5 py-2 bg-slate-50 border border-slate-200 rounded-2xl shadow-2xs">
                  <Clock className="w-4 h-4 text-indigo-600" />
                  <span className="text-xs font-bold text-slate-800 font-mono">{formatTime12Hour(timeSlot)}</span>
                </div>
              )}

              {patientGender && (
                <div className="inline-flex items-center gap-2 px-3.5 py-2 bg-slate-50 border border-slate-200 rounded-2xl shadow-2xs capitalize">
                  <User className="w-4 h-4 text-slate-500" />
                  <span className="text-xs font-bold text-slate-800">{patientGender}</span>
                </div>
              )}

              {patientAge && (
                <div className="inline-flex items-center gap-2 px-3.5 py-2 bg-slate-50 border border-slate-200 rounded-2xl shadow-2xs">
                  <Activity className="w-4 h-4 text-emerald-600" />
                  <span className="text-xs font-bold text-slate-800">{patientAge} yrs</span>
                </div>
              )}
            </div>

            {/* Attached Patient Diagnostic Documents */}
            {patientDocs.length > 0 && (
              <div className="pt-3 border-t border-slate-100 space-y-2">
                <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-1.5">
                  <ExternalLink size={13} className="text-[#143E78]" /> Attached Patient Intake Documents ({patientDocs.length})
                </p>
                <div className="flex flex-wrap gap-2">
                  {patientDocs.map((doc, idx) => (
                    <a
                      key={idx}
                      href={doc.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-3.5 py-2 bg-slate-50 hover:bg-[#143E78]/5 text-[#143E78] rounded-xl text-xs font-bold transition-all border border-slate-200 shadow-2xs hover:border-[#143E78]/30"
                    >
                      <ExternalLink className="w-3.5 h-3.5" />
                      <span className="truncate max-w-[200px]">{doc.name || `Document ${idx + 1}`}</span>
                    </a>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Expandable Clinical Information Section */}
          <div className="border-t border-slate-100 px-5 sm:px-6 py-4 bg-slate-50/50">
            <button
              type="button"
              onClick={() => setShowClinicalInfo(!showClinicalInfo)}
              className="flex items-center justify-between w-full group cursor-pointer"
            >
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-xl bg-indigo-50 text-indigo-600 border border-indigo-100 flex items-center justify-center">
                  <ClipboardList size={16} />
                </div>
                <div className="text-left">
                  <span className="text-xs font-black text-slate-900 uppercase tracking-wider block">Patient Symptoms & Medical History</span>
                  <p className="text-[11px] font-bold text-slate-400">Current complaints, past medical history, and other conditions</p>
                </div>
              </div>
              <div className="w-7 h-7 rounded-lg bg-white border border-slate-200 flex items-center justify-center text-slate-500 group-hover:text-slate-800 transition-colors">
                {showClinicalInfo ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
              </div>
            </button>

            {showClinicalInfo && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3.5 mt-4 pt-3 border-t border-slate-200/60 animate-in fade-in">
                <div className="bg-white rounded-2xl p-3.5 border border-slate-200 space-y-1.5 shadow-2xs">
                  <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest block">Current Symptoms / Complaints</label>
                  <textarea
                    value={formData.chiefComplaints}
                    onChange={(e) => setFormData((prev) => ({ ...prev, chiefComplaints: e.target.value }))}
                    rows={2}
                    className="w-full px-3 py-2 bg-slate-50/60 border border-slate-200 rounded-xl text-xs font-bold text-slate-900 placeholder-slate-400 outline-none focus:border-[#143E78] focus:bg-white focus:ring-4 focus:ring-[#143E78]/10 transition-all resize-none"
                    placeholder="e.g. Severe headache, high fever since 3 days..."
                  />
                </div>
                <div className="bg-white rounded-2xl p-3.5 border border-slate-200 space-y-1.5 shadow-2xs">
                  <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest block">Past Medical History / Illnesses</label>
                  <textarea
                    value={formData.pastMedicalHistory}
                    onChange={(e) => setFormData((prev) => ({ ...prev, pastMedicalHistory: e.target.value }))}
                    rows={2}
                    className="w-full px-3 py-2 bg-slate-50/60 border border-slate-200 rounded-xl text-xs font-bold text-slate-900 placeholder-slate-400 outline-none focus:border-[#143E78] focus:bg-white focus:ring-4 focus:ring-[#143E78]/10 transition-all resize-none"
                    placeholder="e.g. Hypertension, previous surgery, asthma..."
                  />
                </div>
                <div className="bg-white rounded-2xl p-3.5 border border-slate-200 space-y-1.5 shadow-2xs">
                  <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest block">Other Health Conditions / Allergies</label>
                  <textarea
                    value={formData.comorbidities}
                    onChange={(e) => setFormData((prev) => ({ ...prev, comorbidities: e.target.value }))}
                    rows={2}
                    className="w-full px-3 py-2 bg-slate-50/60 border border-slate-200 rounded-xl text-xs font-bold text-slate-900 placeholder-slate-400 outline-none focus:border-[#143E78] focus:bg-white focus:ring-4 focus:ring-[#143E78]/10 transition-all resize-none"
                    placeholder="e.g. Type 2 Diabetes, Penicillin allergy..."
                  />
                </div>
              </div>
            )}
          </div>
        </Card>

        {/* ─── 2. Prescription Items Section (Medications Table) ─────── */}
        <form onSubmit={handleSubmit} className="space-y-6">
          <Card className="p-5 sm:p-6 space-y-5">
            <CardHeader
              icon={Pill}
              iconBg="bg-emerald-50"
              iconColor="text-emerald-700"
              title="Prescription Medications"
              subtitle="Specify drug types, dosage strength, frequency schedule, and meal instructions"
              badge={
                <span className="px-2.5 py-0.5 bg-emerald-100 text-emerald-800 rounded-full text-[10px] font-black">
                  {formData.prescriptionItems.length} Medication(s)
                </span>
              }
            />

            {errors.prescriptionItems && (
              <div className="p-3.5 bg-rose-50 border border-rose-200 rounded-2xl flex items-center gap-2 text-rose-700 text-xs font-bold">
                <AlertCircle size={16} className="shrink-0" />
                <span>{errors.prescriptionItems}</span>
              </div>
            )}

            {/* Desktop Table View - 100% Width Fit (No Horizontal Scrollbar) */}
            <div className="hidden lg:block bg-slate-50/60 rounded-2xl border border-slate-200 overflow-hidden shadow-2xs">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-slate-100/90 border-b border-slate-200 text-[10px] font-black text-slate-500 uppercase tracking-widest">
                    <th className="px-2.5 py-3 text-center w-8">#</th>
                    <th className="px-2.5 py-3 text-left w-28">Type</th>
                    <th className="px-2.5 py-3 text-left">Medicine Name *</th>
                    <th className="px-2.5 py-3 text-left w-32">Strength & Unit</th>
                    <th className="px-2.5 py-3 text-left w-40">Frequency *</th>
                    <th className="px-2.5 py-3 text-left w-32">Duration *</th>
                    <th className="px-2.5 py-3 text-left w-40">Instructions</th>
                    <th className="px-2.5 py-3 text-center w-12">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200 bg-white">
                  {formData.prescriptionItems.map((item, index) => (
                    <PrescriptionTableRow
                      key={item.id}
                      index={index}
                      item={item}
                      errors={errors}
                      customTypeItems={customTypeItems}
                      customFrequencyItems={customFrequencyItems}
                      customDurationItems={customDurationItems}
                      customInstructionsItems={customInstructionsItems}
                      customUnitItems={customUnitItems}
                      onItemChange={handleItemChange}
                      onRemove={removeItem}
                      setCustomType={setCustomTypeItems}
                      setCustomFrequency={setCustomFrequencyItems}
                      setCustomDuration={setCustomDurationItems}
                      setCustomInstructions={setCustomInstructionsItems}
                      setCustomUnit={setCustomUnitItems}
                      canDelete={formData.prescriptionItems.length > 1}
                    />
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile Cards View */}
            <div className="lg:hidden space-y-3">
              {formData.prescriptionItems.map((item, index) => (
                <PrescriptionMobileCard
                  key={item.id}
                  index={index}
                  item={item}
                  errors={errors}
                  customTypeItems={customTypeItems}
                  customFrequencyItems={customFrequencyItems}
                  customDurationItems={customDurationItems}
                  customInstructionsItems={customInstructionsItems}
                  customUnitItems={customUnitItems}
                  onItemChange={handleItemChange}
                  onRemove={removeItem}
                  setCustomType={setCustomTypeItems}
                  setCustomFrequency={setCustomFrequencyItems}
                  setCustomDuration={setCustomDurationItems}
                  setCustomInstructions={setCustomInstructionsItems}
                  setCustomUnit={setCustomUnitItems}
                  canDelete={formData.prescriptionItems.length > 1}
                />
              ))}
            </div>

            {/* Bottom Add Medicine Trigger */}
            <div className="pt-2 flex items-center justify-between border-t border-slate-100">
              <span className="text-xs font-bold text-slate-400">
                {formData.prescriptionItems.length} medicine line item(s) configured
              </span>
              <button
                type="button"
                onClick={addItem}
                className="px-4 py-2.5 bg-slate-100 hover:bg-slate-200/80 text-slate-800 rounded-xl text-xs font-black uppercase tracking-wider transition-all flex items-center gap-1.5 cursor-pointer"
              >
                <Plus size={14} />
                <span>Add Another Medicine</span>
              </button>
            </div>
          </Card>

          {/* ─── 3. Diagnosis & Medical Tests Card ─────────── */}
          <Card className="p-5 sm:p-6 space-y-5">
            <CardHeader
              icon={ClipboardList}
              iconBg="bg-blue-50"
              iconColor="text-blue-700"
              title="Additional Information"
              subtitle="Doctor's diagnosis, recommended lab tests, next follow-up visit, and diet advice"
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest block">Diagnosis / Health Condition</label>
                <input
                  type="text"
                  value={formData.provisionalDiagnosis}
                  onChange={(e) => setFormData((prev) => ({ ...prev, provisionalDiagnosis: e.target.value }))}
                  placeholder="e.g. Viral Fever / Acute Bronchitis / Grade II Gallstones"
                  className="w-full px-3.5 py-3 bg-slate-50/70 border border-slate-200 rounded-xl text-xs font-bold text-slate-900 placeholder-slate-400 outline-none focus:border-[#143E78] focus:bg-white focus:ring-4 focus:ring-[#143E78]/10 transition-all"
                />
              </div>

              <div className="space-y-1.5">
                <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest block">Next Follow-Up / Visit</label>
                <input
                  type="text"
                  value={formData.followUp}
                  onChange={(e) => setFormData((prev) => ({ ...prev, followUp: e.target.value }))}
                  placeholder="e.g. Review after 5 Days, or as needed if symptoms persist"
                  className="w-full px-3.5 py-3 bg-slate-50/70 border border-slate-200 rounded-xl text-xs font-bold text-slate-900 placeholder-slate-400 outline-none focus:border-[#143E78] focus:bg-white focus:ring-4 focus:ring-[#143E78]/10 transition-all"
                />
              </div>
            </div>

            {/* Diagnostic Investigations & Lab Tests */}
            <div className="space-y-2.5 pt-3 border-t border-slate-100">
              <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest block">
                Recommended Lab Tests & Scans (Investigations)
              </label>

              <div className="relative">
                <div className="flex items-center gap-2">
                  <div className="relative flex-1">
                    <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
                    <input
                      type="text"
                      value={investigationSearch}
                      onChange={(e) => setInvestigationSearch(capitalizeFirst(e.target.value))}
                      onFocus={() => setShowInvestigationSuggestions(true)}
                      onBlur={() => setTimeout(() => setShowInvestigationSuggestions(false), 200)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          const trimmed = investigationSearch.trim();
                          if (!trimmed) return;
                          const exactMatch = ALL_INVESTIGATIONS.find(
                            (inv) => inv.label.toLowerCase() === trimmed.toLowerCase()
                          );
                          if (exactMatch) {
                            addInvestigation(exactMatch);
                          } else if (trimmed) {
                            addInvestigation({
                              id: `custom_${Date.now()}`,
                              label: trimmed,
                              category: 'Custom',
                              icon: '📋',
                              categoryKey: 'OTHER'
                            });
                          }
                        }
                      }}
                      placeholder="Search tests (e.g. Blood Test CBC, Thyroid, USG Abdomen, X-Ray) or type any test..."
                      className="w-full pl-10 pr-4 py-3 bg-slate-50/70 border border-slate-200 rounded-xl text-xs font-bold text-slate-900 placeholder-slate-400 outline-none focus:border-[#143E78] focus:bg-white focus:ring-4 focus:ring-[#143E78]/10 transition-all"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      const trimmed = investigationSearch.trim();
                      if (!trimmed) return;
                      const exactMatch = ALL_INVESTIGATIONS.find(
                        (inv) => inv.label.toLowerCase() === trimmed.toLowerCase()
                      );
                      if (exactMatch) {
                        addInvestigation(exactMatch);
                      } else if (trimmed) {
                        addInvestigation({
                          id: `custom_${Date.now()}`,
                          label: trimmed,
                          category: 'Custom',
                          icon: '📋',
                          categoryKey: 'OTHER'
                        });
                      }
                    }}
                    className="px-5 py-3 bg-[#143E78] text-white rounded-xl text-xs font-black uppercase tracking-wider hover:bg-[#0f2c56] transition-all flex items-center gap-1.5 shadow-sm cursor-pointer shrink-0"
                  >
                    <Plus size={15} />
                    <span>Add Test</span>
                  </button>
                </div>

                {/* Suggestions Dropdown */}
                {showInvestigationSuggestions && filteredInvestigations.length > 0 && (
                  <div className="absolute z-40 w-full mt-2 bg-white border-2 border-slate-100 rounded-2xl shadow-xl max-h-80 overflow-y-auto divide-y divide-slate-100">
                    {Object.entries(groupedInvestigations).map(([key, group]) => (
                      <div key={key}>
                        <div className="px-4 py-2 bg-slate-50 text-[10px] font-black text-slate-500 uppercase tracking-widest sticky top-0">
                          {group.icon} {group.label}
                        </div>
                        {group.items.map((item) => (
                          <button
                            key={item.id}
                            type="button"
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => addInvestigation(item)}
                            className="w-full flex items-center justify-between px-4 py-2.5 hover:bg-[#143E78]/5 transition-colors text-left cursor-pointer"
                          >
                            <span className="text-xs font-bold text-slate-800">{item.label}</span>
                            <span className="text-[10px] font-black text-[#143E78] bg-[#143E78]/10 px-2.5 py-0.5 rounded-lg uppercase">
                              {item.category}
                            </span>
                          </button>
                        ))}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Selected Investigation Tags */}
              {formData.investigations.length > 0 && (
                <div className="flex flex-wrap gap-2 pt-2">
                  {formData.investigations.map((inv, index) => (
                    <div
                      key={inv.id || index}
                      className="inline-flex items-center gap-2 px-3 py-1.5 bg-blue-50 border border-blue-200 rounded-xl text-blue-900 shadow-2xs animate-in fade-in"
                    >
                      <span className="text-sm">{inv.icon || '📋'}</span>
                      <div>
                        <p className="text-xs font-black text-[#143E78]">{inv.label}</p>
                        <p className="text-[9px] font-bold text-slate-500 uppercase">{inv.category}</p>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeInvestigation(index)}
                        className="inline-flex items-center justify-center w-5 h-5 text-rose-500 hover:text-rose-700 hover:bg-rose-100/60 rounded-md transition-colors ml-1 cursor-pointer"
                        title="Remove"
                      >
                        <X size={13} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Advice & Extra Clinical Notes */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-3 border-t border-slate-100">
              <div className="space-y-2">
                <button
                  type="button"
                  onClick={() => setShowAdvice(!showAdvice)}
                  className="flex items-center justify-between w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs font-black text-slate-800 hover:bg-slate-100 transition-all cursor-pointer"
                >
                  <span className="flex items-center gap-2">
                    <Sparkles size={14} className="text-amber-500" /> Patient Diet & Lifestyle Advice
                  </span>
                  {showAdvice ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                </button>
                {showAdvice && (
                  <textarea
                    value={formData.advice}
                    onChange={(e) => setFormData((prev) => ({ ...prev, advice: e.target.value }))}
                    rows={3}
                    placeholder="Enter diet guidelines, foods to avoid, hydration, exercise, or rest advice for the patient..."
                    className="w-full px-3.5 py-2.5 bg-white border border-slate-200 rounded-xl text-xs font-bold text-slate-900 placeholder-slate-400 outline-none focus:border-[#143E78] focus:ring-4 focus:ring-[#143E78]/10 transition-all resize-none"
                  />
                )}
              </div>

              <div className="space-y-2">
                <button
                  type="button"
                  onClick={() => setShowNotes(!showNotes)}
                  className="flex items-center justify-between w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs font-black text-slate-800 hover:bg-slate-100 transition-all cursor-pointer"
                >
                  <span className="flex items-center gap-2">
                    <Info size={14} className="text-indigo-500" /> Doctor's Internal Notes / Remarks
                  </span>
                  {showNotes ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                </button>
                {showNotes && (
                  <textarea
                    value={formData.notes}
                    onChange={(e) => setFormData((prev) => ({ ...prev, notes: e.target.value }))}
                    rows={3}
                    placeholder="Add private observations or special notes for the medical and CRM team..."
                    className="w-full px-3.5 py-2.5 bg-white border border-slate-200 rounded-xl text-xs font-bold text-slate-900 placeholder-slate-400 outline-none focus:border-[#143E78] focus:ring-4 focus:ring-[#143E78]/10 transition-all resize-none"
                  />
                )}
              </div>
            </div>
          </Card>

          {/* ─── 4. Footer Submit Action Bar ──────────────────────────── */}
          <div className="bg-white rounded-3xl border-2 border-slate-100 p-5 shadow-sm flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="flex items-center gap-2 text-xs font-bold text-slate-500">
              <ShieldCheck size={16} className="text-emerald-600" />
              <span>All changes will be securely attached to this patient case file</span>
            </div>

            <div className="flex items-center gap-3">
              <button
                type="submit"
                disabled={submitting}
                className="px-8 py-3.5 bg-[#143E78] hover:bg-[#0f2c56] text-white rounded-2xl text-xs font-black uppercase tracking-wider transition-all flex items-center justify-center gap-2 shadow-lg shadow-[#143E78]/25 disabled:opacity-50 cursor-pointer"
              >
                {submitting ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
                <span>{submitting ? 'Submitting Prescription...' : 'Submit & Save Prescription'}</span>
              </button>
            </div>
          </div>
        </form>
      </main>
    </div>
  );
};

// ─── Sub-Components ──────────────────────────────────────────────────────────
const SelectField = ({
  label,
  value,
  onChange,
  options = [],
  customKey,
  customItems = {},
  onCustomToggle,
  placeholder = 'Select...',
  className = '',
  isAttachedLeft = false
}) => {
  const isCustom = Boolean(customItems[customKey]);
  const displayOptions = options.filter((f) => f.value !== 'other');
  const hasMatch = displayOptions.some((f) => f.value === value);
  const showCustom = isCustom || (Boolean(value) && !hasMatch);

  const roundedClasses = isAttachedLeft ? 'rounded-r-lg rounded-l-none border-l-0' : 'rounded-lg';

  const handleSelectChange = (e) => {
    const val = e.target.value;
    if (val === 'other') {
      if (onCustomToggle) onCustomToggle(customKey, true);
      onChange('');
    } else {
      if (onCustomToggle) onCustomToggle(customKey, false);
      onChange(val);
    }
  };

  const handleCustomChange = (e) => {
    onChange(e.target.value);
  };

  const handleBackToSelect = () => {
    if (onCustomToggle) onCustomToggle(customKey, false);
    onChange('');
  };

  return (
    <div className={className}>
      {!showCustom ? (
        <div className="relative">
          <select
            value={hasMatch ? value : ''}
            onChange={handleSelectChange}
            className={`w-full h-8 px-2 bg-white border border-slate-200 ${roundedClasses} text-xs font-bold text-slate-800 outline-none focus:border-[#143E78] focus:ring-2 focus:ring-[#143E78]/10 transition-all appearance-none cursor-pointer pr-5`}
          >
            <option value="">{placeholder}</option>
            {displayOptions.map((f) => (
              <option key={f.value} value={f.value}>{f.label}</option>
            ))}
            <option value="other">+ Custom / Other</option>
          </select>
          <ChevronDown className="absolute right-1.5 top-1/2 -translate-y-1/2 w-3 h-3 text-slate-400 pointer-events-none" />
        </div>
      ) : (
        <div className="relative">
          <input
            type="text"
            value={value || ''}
            onChange={handleCustomChange}
            placeholder={`Custom ${label || placeholder || ''}...`}
            className={`w-full h-8 px-2 pr-6 bg-white border border-[#143E78] ${roundedClasses} text-xs font-bold text-slate-800 placeholder-slate-400 outline-none focus:ring-2 focus:ring-[#143E78]/10 transition-all`}
            autoFocus
          />
          <button
            type="button"
            onClick={handleBackToSelect}
            className="absolute right-1 top-1/2 -translate-y-1/2 inline-flex items-center justify-center w-5 h-5 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded transition-colors cursor-pointer"
            title="Switch back to dropdown list"
          >
            <X size={12} />
          </button>
        </div>
      )}
    </div>
  );
};

const PrescriptionTableRow = ({
  index,
  item,
  errors,
  customTypeItems,
  customFrequencyItems,
  customDurationItems,
  customInstructionsItems,
  customUnitItems,
  onItemChange,
  onRemove,
  setCustomType,
  setCustomFrequency,
  setCustomDuration,
  setCustomInstructions,
  setCustomUnit,
  canDelete
}) => {
  return (
    <tr className="hover:bg-slate-50/70 transition-colors">
      <td className="px-2.5 py-2.5 text-center text-xs font-black text-slate-400">{index + 1}</td>
      <td className="px-2.5 py-2.5">
        <SelectField
          label=""
          value={item.type}
          onChange={(val) => onItemChange(index, 'type', val)}
          options={TYPE_OPTIONS}
          customKey={`type_${index}`}
          customItems={customTypeItems}
          onCustomToggle={(key, val) => setCustomType((prev) => ({ ...prev, [key]: val }))}
          placeholder="Select type"
          className="w-full"
        />
      </td>
      <td className="px-2.5 py-2.5">
        <input
          type="text"
          value={item.medicineName}
          onChange={(e) => onItemChange(index, 'medicineName', e.target.value)}
          className="w-full h-8 px-2.5 bg-white border border-slate-200 rounded-lg text-xs font-bold text-slate-900 placeholder-slate-400 outline-none focus:border-[#143E78] focus:ring-2 focus:ring-[#143E78]/10 transition-all"
          placeholder="e.g. Paracetamol / Amoxicillin"
        />
        {errors[`item_${index}_medicineName`] && (
          <p className="mt-1 text-[10px] font-bold text-rose-600 uppercase tracking-wider">{errors[`item_${index}_medicineName`]}</p>
        )}
      </td>
      <td className="px-2.5 py-2.5">
        <div className="flex items-center">
          <input
            type="text"
            value={item.strength}
            onChange={(e) => onItemChange(index, 'strength', e.target.value)}
            className="w-14 h-8 px-1.5 text-center bg-white border border-slate-200 rounded-l-lg text-xs font-bold text-slate-900 placeholder-slate-400 outline-none focus:border-[#143E78] transition-all shrink-0"
            placeholder="500"
          />
          <SelectField
            value={item.unit}
            onChange={(val) => onItemChange(index, 'unit', val)}
            options={UNIT_OPTIONS}
            customKey={`unit_${index}`}
            customItems={customUnitItems}
            onCustomToggle={(key, val) => setCustomUnit((prev) => ({ ...prev, [key]: val }))}
            placeholder="mg"
            className="w-20 shrink-0"
            isAttachedLeft={true}
          />
        </div>
      </td>
      <td className="px-2.5 py-2.5">
        <SelectField
          label=""
          value={item.frequency}
          onChange={(val) => onItemChange(index, 'frequency', val)}
          options={FREQUENCY_OPTIONS}
          customKey={`frequency_${index}`}
          customItems={customFrequencyItems}
          onCustomToggle={(key, val) => setCustomFrequency((prev) => ({ ...prev, [key]: val }))}
          placeholder="Select frequency"
          required
          className="w-full"
        />
        {errors[`item_${index}_frequency`] && (
          <p className="mt-1 text-[10px] font-bold text-rose-600 uppercase tracking-wider">{errors[`item_${index}_frequency`]}</p>
        )}
      </td>
      <td className="px-2.5 py-2.5">
        <div className="flex items-center">
          <input
            type="number"
            value={item.duration}
            onChange={(e) => onItemChange(index, 'duration', e.target.value)}
            className="w-12 h-8 px-1 text-center bg-white border border-slate-200 rounded-l-lg text-xs font-bold text-slate-900 placeholder-slate-400 outline-none focus:border-[#143E78] transition-all shrink-0"
            placeholder="3"
            min="0"
            step="any"
          />
          <SelectField
            value={item.durationUnit}
            onChange={(val) => onItemChange(index, 'durationUnit', val)}
            options={DURATION_UNIT_OPTIONS}
            customKey={`durationUnit_${index}`}
            customItems={customDurationItems}
            onCustomToggle={(key, val) => setCustomDuration((prev) => ({ ...prev, [key]: val }))}
            placeholder="days"
            className="w-20 shrink-0"
            isAttachedLeft={true}
          />
        </div>
        {errors[`item_${index}_duration`] && (
          <p className="mt-1 text-[10px] font-bold text-rose-600 uppercase tracking-wider">{errors[`item_${index}_duration`]}</p>
        )}
      </td>
      <td className="px-2.5 py-2.5">
        <SelectField
          label=""
          value={item.instructions}
          onChange={(val) => onItemChange(index, 'instructions', val)}
          options={INSTRUCTIONS_OPTIONS}
          customKey={`instructions_${index}`}
          customItems={customInstructionsItems}
          onCustomToggle={(key, val) => setCustomInstructions((prev) => ({ ...prev, [key]: val }))}
          placeholder="Select instruction"
          className="w-full"
        />
      </td>
      <td className="px-2.5 py-2.5 text-center">
        {canDelete ? (
          <button
            type="button"
            onClick={() => onRemove(item.id)}
            className="inline-flex items-center justify-center w-7 h-7 text-rose-500 hover:text-rose-700 hover:bg-rose-50 rounded-lg transition-colors cursor-pointer"
            title="Delete Medicine"
          >
            <Trash2 size={14} />
          </button>
        ) : (
          <span className="text-slate-300">—</span>
        )}
      </td>
    </tr>
  );
};

const PrescriptionMobileCard = ({
  index,
  item,
  errors,
  customTypeItems,
  customFrequencyItems,
  customDurationItems,
  customInstructionsItems,
  customUnitItems,
  onItemChange,
  onRemove,
  setCustomType,
  setCustomFrequency,
  setCustomDuration,
  setCustomInstructions,
  setCustomUnit,
  canDelete
}) => {
  return (
    <div className="bg-slate-50/70 border border-slate-200 rounded-2xl p-4 space-y-3 shadow-2xs">
      <div className="flex items-center justify-between border-b border-slate-200/80 pb-2">
        <span className="text-xs font-black text-slate-800 uppercase tracking-wider">
          Medicine #{index + 1}
        </span>
        {canDelete && (
          <button
            type="button"
            onClick={() => onRemove(item.id)}
            className="inline-flex items-center justify-center w-6 h-6 text-rose-500 hover:text-rose-700 hover:bg-rose-100/50 rounded-md transition-colors cursor-pointer"
            title="Delete"
          >
            <Trash2 size={13} />
          </button>
        )}
      </div>

      <div className="space-y-2.5">
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="block text-[10px] font-black text-slate-500 uppercase tracking-widest mb-1">Type</label>
            <SelectField
              value={item.type}
              onChange={(val) => onItemChange(index, 'type', val)}
              options={TYPE_OPTIONS}
              customKey={`type_${index}`}
              customItems={customTypeItems}
              onCustomToggle={(key, val) => setCustomType((prev) => ({ ...prev, [key]: val }))}
              placeholder="Select type"
            />
          </div>
          <div>
            <label className="block text-[10px] font-black text-slate-500 uppercase tracking-widest mb-1">Medicine Name *</label>
            <input
              type="text"
              value={item.medicineName}
              onChange={(e) => onItemChange(index, 'medicineName', e.target.value)}
              className="w-full h-8 px-2.5 bg-white border border-slate-200 rounded-lg text-xs font-bold text-slate-900 placeholder-slate-400 outline-none focus:border-[#143E78] transition-all"
              placeholder="e.g. Paracetamol"
            />
            {errors[`item_${index}_medicineName`] && (
              <p className="mt-1 text-[10px] font-bold text-rose-600 uppercase tracking-wider">{errors[`item_${index}_medicineName`]}</p>
            )}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="block text-[10px] font-black text-slate-500 uppercase tracking-widest mb-1">Strength</label>
            <input
              type="text"
              value={item.strength}
              onChange={(e) => onItemChange(index, 'strength', e.target.value)}
              className="w-full h-8 px-2 text-center bg-white border border-slate-200 rounded-lg text-xs font-bold text-slate-900 placeholder-slate-400 outline-none focus:border-[#143E78] transition-all"
              placeholder="500"
            />
          </div>
          <div>
            <label className="block text-[10px] font-black text-slate-500 uppercase tracking-widest mb-1">Unit</label>
            <SelectField
              value={item.unit}
              onChange={(val) => onItemChange(index, 'unit', val)}
              options={UNIT_OPTIONS}
              customKey={`unit_${index}`}
              customItems={customUnitItems}
              onCustomToggle={(key, val) => setCustomUnit((prev) => ({ ...prev, [key]: val }))}
              placeholder="mg"
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="block text-[10px] font-black text-slate-500 uppercase tracking-widest mb-1">Frequency *</label>
            <SelectField
              value={item.frequency}
              onChange={(val) => onItemChange(index, 'frequency', val)}
              options={FREQUENCY_OPTIONS}
              customKey={`frequency_${index}`}
              customItems={customFrequencyItems}
              onCustomToggle={(key, val) => setCustomFrequency((prev) => ({ ...prev, [key]: val }))}
              placeholder="Frequency"
            />
            {errors[`item_${index}_frequency`] && (
              <p className="mt-1 text-[10px] font-bold text-rose-600 uppercase tracking-wider">{errors[`item_${index}_frequency`]}</p>
            )}
          </div>
          <div>
            <label className="block text-[10px] font-black text-slate-500 uppercase tracking-widest mb-1">Duration *</label>
            <div className="flex items-center gap-1">
              <input
                type="number"
                value={item.duration}
                onChange={(e) => onItemChange(index, 'duration', e.target.value)}
                className="w-16 h-8 px-2 text-center bg-white border border-slate-200 rounded-lg text-xs font-bold text-slate-900 placeholder-slate-400 outline-none focus:border-[#143E78] transition-all"
                placeholder="3"
                min="0"
                step="any"
              />
              <div className="flex-1">
                <SelectField
                  value={item.durationUnit}
                  onChange={(val) => onItemChange(index, 'durationUnit', val)}
                  options={DURATION_UNIT_OPTIONS}
                  customKey={`durationUnit_${index}`}
                  customItems={customDurationItems}
                  onCustomToggle={(key, val) => setCustomDuration((prev) => ({ ...prev, [key]: val }))}
                  placeholder="days"
                />
              </div>
            </div>
            {errors[`item_${index}_duration`] && (
              <p className="mt-1 text-[10px] font-bold text-rose-600 uppercase tracking-wider">{errors[`item_${index}_duration`]}</p>
            )}
          </div>
        </div>

        <div>
          <label className="block text-[10px] font-black text-slate-500 uppercase tracking-widest mb-1">Instruction</label>
          <SelectField
            value={item.instructions}
            onChange={(val) => onItemChange(index, 'instructions', val)}
            options={INSTRUCTIONS_OPTIONS}
            customKey={`instructions_${index}`}
            customItems={customInstructionsItems}
            onCustomToggle={(key, val) => setCustomInstructions((prev) => ({ ...prev, [key]: val }))}
            placeholder="Select instruction"
          />
        </div>
      </div>
    </div>
  );
};

export default PrescriptionFormPublic;